import React from 'react';
import RoddoSectionCard from './layout/RoddoSectionCard';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import tournamentsComingSoonBg from '../assets/backgrounds/tournamentscomingsoon.png';

type Props = {
  onBack?: () => void;
  logoNode?: React.ReactNode;
  isDarkMode?: boolean;
  onEnterPreview?: () => void;
};

export default function TournamentsComingSoonView({
  onBack,
  logoNode,
  isDarkMode,
  onEnterPreview,
}: Props) {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(dark);

  const cleanLogoNode = React.isValidElement<{
    className?: string;
    style?: React.CSSProperties;
  }>(logoNode)
    ? React.cloneElement(logoNode, {
        className: 'h-9 w-12 object-contain',
        style: {
          width: 48,
          height: 36,
          maxWidth: 48,
          maxHeight: 36,
          objectFit: 'contain',
          objectPosition: 'center',
        },
      })
    : logoNode;

  return (
    <div
      className="relative h-full min-h-0 w-full overflow-hidden"
      style={{ color: ui.textPrimary }}
    >
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={tournamentsComingSoonBg}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: 'center center' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: dark
              ? 'radial-gradient(90% 60% at 50% 0%, rgba(118,87,232,0.12), transparent 60%), linear-gradient(180deg, rgba(9,11,19,0.10), rgba(14,16,32,0.24), rgba(9,11,19,0.52))'
              : 'linear-gradient(180deg, rgba(255,247,235,0.02), rgba(255,247,235,0.08), rgba(255,239,221,0.20))',
          }}
        />
      </div>

      <div
        className="relative z-10 flex h-full min-h-0 flex-col justify-between px-5 pt-10 sm:px-6 sm:pt-12"
        style={{
          paddingBottom: 'calc(var(--roddo-bottom-nav-h, var(--roddo-nav-h, 76px)) + 18px)',
        }}
      >
        <RoddoSectionCard className="mx-auto mt-2 w-full max-w-sm shrink-0 border-white/35 bg-white/72 px-5 py-5 text-center shadow-2xl backdrop-blur-md">
          <div className="flex items-center justify-center gap-4">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[22px] border-2 bg-white/75"
              style={{
                borderColor: ui.borderStrong,
                boxShadow: `${ui.shadow}, inset 0 2px 0 rgba(255,255,255,0.24)`,
              }}
            >
              <span className="flex h-9 w-12 items-center justify-center overflow-visible">
                {cleanLogoNode || (
                  <span className="text-3xl leading-none" aria-hidden="true">
                    🐟
                  </span>
                )}
              </span>
            </div>

            <div className="min-w-0 text-left">
              <div
                className="text-xl font-extrabold leading-tight tracking-tight"
                style={{ color: ui.textPrimary }}
              >
                Roddo Tournaments
              </div>
              <div
                className="mt-1 text-[14px] font-black uppercase tracking-[0.28em]"
                style={{ color: ui.textPrimary, opacity: 0.92 }}
              >
                Coming Soon
              </div>
            </div>
          </div>
        </RoddoSectionCard>

        <RoddoSectionCard className="mx-auto mb-0 w-full max-w-sm shrink-0 border-white/35 bg-white/72 px-5 py-5 text-center shadow-2xl backdrop-blur-md">
          <div
            className="text-sm font-semibold leading-6"
            style={{ color: ui.textSecondary }}
          >
            We’re polishing tournaments, badges, and leaderboard play for launch.
          </div>

          {(onBack || onEnterPreview) ? (
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {onEnterPreview ? <button type="button" onClick={onEnterPreview} className="inline-flex items-center justify-center rounded-2xl border px-4 py-2.5 font-black text-white" style={{background: dark ? '#7057E8' : '#2F80ED', borderColor: dark ? 'rgba(255,255,255,.24)' : 'rgba(47,128,237,.32)'}}>Enter Tournament Preview</button> : null}
              <button
                type="button"
                onClick={onBack}
                className="inline-flex min-w-[96px] items-center justify-center rounded-2xl border px-4 py-2.5 font-semibold transition-transform active:translate-y-[1px]"
                style={{
                  background: dark ? 'rgba(27,24,48,0.88)' : 'rgba(255,255,255,0.62)',
                  borderColor: ui.borderStrong,
                  color: ui.textPrimary,
                  boxShadow: `${ui.shadow}, inset 0 2px 0 rgba(255,255,255,0.22)`,
                }}
              >
                Back
              </button>
            </div>
          ) : null}
        </RoddoSectionCard>
      </div>
    </div>
  );
}
